/// <reference path="./PpdBaseTask.ts" />
import { TClientApp } from "@ogaf/scripts/Base/App";
import { RibbonModel } from "@ogaf/scripts/Base/AppInterfaces";

/**
 * Třída TPpdApp rozšiřuje základní klientskou aplikaci o specifické chování pro modul Popis sítě.
 */
export class TPpdApp extends TClientApp {
    /**
     * Formátuje tasky pro ribbon.
     * Pokud nejsou žádné položky, přidá defaultní submodul a task pro Popis sítě.
     * @param items Pole modelů ribbonu.
     * @returns Formátovaný řetězec s úkoly pro ribbon.
     */
    public override FormatTasks(items: RibbonModel[]): string {
        if (items.length !== 0) {
            return super.FormatTasks(items);
        }
        const tabPanelInstance = $("#mainRibbon-container").dxTabPanel("instance");
        const rawSelectedItemKeys = (tabPanelInstance?.option("selectedItemKeys") ?? []) as Array<string | { Key?: string }>;
        const keys: string[] = rawSelectedItemKeys.map(k => typeof k === "string" ? k : (k.Key ?? "")).filter(k => k.length > 0);

        if (!keys.includes("modPopisSite")) {
            return super.FormatTasks(items); 
        }

        const task: RibbonModel = {
            Caption: "SPO hranice",
            Hint: "SPO hranice",
            Icon: "sestava_32x32_png",
            Key: "taskHranice",
            Parent: "",
            Type: "Task",
            ChildComponents: [],
            IsActive: false
        };

        const submodule: RibbonModel = {
            Caption: "Tratě",
            Hint: "",
            Icon: "",
            Key: "modSubTrate",
            Parent: "",
            Type: "SubModule",
            ChildComponents: [task],
            IsActive: false
        };

        return super.FormatTasks([submodule]);
    }

    /**
     * Handler pro událost načtení obsahu ribbonu.
     * Přidá modul Popis sítě do ribbonu, pokud ještě neexistuje.
     * @param e Událost načtení obsahu komponenty dxTabPanel.
     */
    public override mainRibbonOnContentReady(e: DevExpress.ui.dxTabPanel.ContentReadyEvent, showAppMenu: string, currentModuleIndex: string) : void {
        super.mainRibbonOnContentReady(e, showAppMenu, currentModuleIndex);
        const instance = e.component.instance();
        const items = instance.getDataSource()?.items() as RibbonModel[];
        if (!items) return;
        const exists = items.some(i => i.Key === "modPopisSite");
        if (!exists) {
            const newItem: RibbonModel = {
                Caption: "Popis sítě",
                Hint: "",
                Icon: "",
                Key: "modPopisSite",
                Parent: "",
                Type: "Module",
                ChildComponents: [],
                IsActive: false
            };
            instance.option("dataSource", [...items, newItem]);
        }
    }

    /**
     * Zpracuje kliknutí na položku ribbonu
     *
     * Prefix "link" je určen pro položky, které se mají otevřít v novém okně,
     * aby bylo možné následně provést redirect ve view daného tasku
     * nebo zobrazit externí aplikaci odděleně od hlavního okna PPD.
     *
     * @param key Klíč kliknuté položky ribbonu.
     */
    public override OnRibbonTabItemAction(key: string): void {
        const prefix = key.substr(0, 4).toLowerCase();

        if (prefix === "task") // otevře task ve stávajícím okně
        {
            this.switchTask(key, "");
        }
        else if (prefix === "link") // otevře task v novém okně / panelu
        {
            this.openLinkInNewWindow(key);
        }
        else // deleguje akci na aktuální task
        {
            const page = this.PageName();
            if (key.toString().startsWith("btn") || key.toString().startsWith("fn")) {
                this.CurrentTask.dispatchAction(key, page);
            }
        }
    }

    /**
     * Otevře link v novém okně / kartě prohlížeče.
     *
     * Z TaskMap vyhledá odpovídající link a otevře jeho cílovou URL v novém okně.
     *
     * @param linkName Klíč linku z TaskMap.
     */
    public openLinkInNewWindow(linkName: string): void {
        const link = this.TaskMap.find(x => x.key === linkName);
        if (link === undefined) {
            console.warn(`Link '${linkName}' nebyl nalezen v TaskMap.`);
            return;
        }

        const newWindow = window.open(link.uri, "_blank", "noopener,noreferrer");
        if (!newWindow) {
            console.warn("Prohlížeč zablokoval otevření nového okna.");
        }
    }

    /**
     * Zobrazí nápovědu v novém okně prohlížeče.
     *
     * Postupně ověří dostupnost souborů nápovědy na definovaných cestách
     * pomocí synchronního HEAD požadavku. První nalezený soubor otevře
     * v novém okně s pevně danými rozměry a pozicí.
     */
    public override showHelp() : void {
        const x = window.screenX;
        const y = window.screenY;
        const paths = ["/Help/index.html", "/Help/Help.html"];
        let path = "";

        for (let i = 0; i < 2; i++) {
            let http = new XMLHttpRequest();
            http.open('HEAD', paths[i], false)
            http.send();
            if (http.status === 200) {
                path = paths[i]; break;
            }
        }

        if (path != "") {
            const helpwin = window.open(path, "_blank", "directories=no,width=800,height=600,menubar=no,resizable=yes,scrollbars=yes,location=no");
            helpwin.moveTo(x + 60, y + 100);
        }
    }

    /**
     * Obnoví pohled aplikace.
     * @remarks
     * Tato metoda je definována z důvodu kompatibility s funkcionalitou modulu ETCS.
     * Plné znění metody je implementováno v modulu ETCS.
     */
    public ViewRefresh() : void { }
}